import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { useTicketsTable } from '@services/tickets/queries/useTicketsTable'
import type { TicketList } from '@services/tickets/types'
import { MessageSquare, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Combobox } from '@/components/ui/combobox'
import { Label } from '@/components/ui/label'

import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  TICKET_STATUSES
} from '../../constants'
import { TicketPriorityPill, TicketStatusPill } from '../../components/TicketPills'
import { useIntlLocale } from '@/i18n/languages'

function FilterSelect({
  param,
  label,
  values,
  labelPrefix
}: {
  param: string
  label: string
  values: string[]
  labelPrefix: string
}) {
  const { t } = useTranslation('tickets')
  const [searchParams, setSearchParams] = useSearchParams()

  return (
    <div className="w-48">
      <Label className="mb-1.5 block">{label}</Label>
      <Combobox
        searchable={false}
        clearable
        value={searchParams.get(param) ?? undefined}
        onChange={(value) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            if (value) next.set(param, value)
            else next.delete(param)
            next.delete('offset')
            return next
          })
        }}
        options={values.map((value) => ({
          value,
          label: t(`${labelPrefix}.${value}`)
        }))}
      />
    </div>
  )
}

export function TicketsTable() {
  const { t } = useTranslation('tickets')
  const query = useTicketsTable()
  const locale = useIntlLocale()

  const columns = [
    {
      title: t('admin.sections.title'),
      dataIndex: 'title',
      render: (value: string, record: TicketList) => (
        <div className="min-w-0">
          <span className="font-medium text-ink">{value}</span>
          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
            {t(`categories.${record.category}`)}
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {record.comments_count}
            </span>
          </div>
        </div>
      )
    },
    {
      title: t('admin.sections.tenant'),
      dataIndex: 'user',
      render: (value: TicketList['user']) => (
        <Link
          to={`/admin/users/${value.id}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
        >
          <User className="h-3.5 w-3.5" />
          {`${value.first_name ?? ''} ${value.last_name ?? ''}`.trim() ||
            value.email}
        </Link>
      )
    },
    {
      title: t('admin.sections.flat'),
      dataIndex: 'flat',
      render: (value: TicketList['flat']) =>
        value?.id ? (
          <Link
            to={`/admin/flats/${value.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-ink hover:text-primary"
          >
            {value.title}
          </Link>
        ) : (
          '-'
        )
    },
    {
      title: t('admin.sections.priority'),
      dataIndex: 'priority',
      render: (value: TicketList['priority']) => (
        <TicketPriorityPill priority={value} />
      )
    },
    {
      title: t('admin.sections.status'),
      dataIndex: 'status',
      render: (value: TicketList['status']) => <TicketStatusPill status={value} />
    },
    {
      title: t('admin.sections.created'),
      dataIndex: 'created_at',
      render: (value: string) => new Date(value).toLocaleDateString(locale)
    }
  ]

  return (
    <div className="space-y-6">
      <AnimatedPageHeader title={t('admin.title')} />
      <div className="flex flex-wrap gap-4">
        <FilterSelect
          param="status"
          label={t('admin.filters.status')}
          values={TICKET_STATUSES}
          labelPrefix="statuses"
        />
        <FilterSelect
          param="category"
          label={t('admin.filters.category')}
          values={TICKET_CATEGORIES}
          labelPrefix="categories"
        />
        <FilterSelect
          param="priority"
          label={t('admin.filters.priority')}
          values={TICKET_PRIORITIES}
          labelPrefix="priorities"
        />
      </div>
      <Table<PageRead<TicketList>>
        query={query}
        columns={columns}
        id="tickets"
        hasSearch={false}
      />
    </div>
  )
}
